/**
 * JS client library for detecting if a user has been served a
 * one-time promotion of any kind.
 *
 * Corresponds to PromoRecord in promo_record_model.py on the server side.
 */
import gql from "graphql-tag";

import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";

import {_internalAddPromoToCache} from "./has-promo-been-seen.ts";

export const MARK_PROMO_AS_SEEN_MUTATION: GqlOperation<Data, Variables> = gqlOp(
    gql`
        mutation markPromoAsSeen($promoName: String!, $kaid: String!) {
            recordPromo(promoName: $promoName, kaid: $kaid) {
                registrationSuccess
                error {
                    code
                }
            }
        }
    `,
);

type Data = {
    error:
        | {
              code: "INTERNAL" | "NOT_FOUND" | "UNAUTHORIZED";
          }
        | null
        | undefined;
};

type Variables = {
    promoName: string;
    kaid: string;
};

/**
 * Marks a promo as having been served.
 * @param {string} gqlClient Gql Client to make call.
 * @param {string} promoName The name of the promo mark.
 * @param {string} kaid The user kaid that has seen the promo.
 **/

export const markAsSeen = async (
    gqlClient: ApolloGqlClient,
    promoName: string,
    kaid: string,
): Promise<boolean> => {
    _internalAddPromoToCache(promoName);

    const variables = {
        promoName: promoName,
        kaid: kaid,
    } as const;

    const result = await gqlClient.mutate(MARK_PROMO_AS_SEEN_MUTATION, {
        variables,
    });

    if (result?.data?.error) {
        return false;
    }

    return true;
};
